import {Injectable} from '@angular/core';
import {firstValueFrom, merge, switchMap} from 'rxjs';
import {filter, map} from 'rxjs/operators';
import {FaultData} from '@docma-mm-core/fault/fault-data';
import {ViewFault} from '@docma-mm-core/fault/fault';
import {Structure} from '@docma-mm-core/structure/structure';
import {Attachment, AttachmentData} from '@docma-mm-core/attachment/attachment';
import {AttachmentType} from '@docma-mm-core/attachment/attachment-type';
import {Logger} from '@mydocma/common/src/lib/logger';
import {FaultDuplicateWizardStepperService, IDuplicateWizardStepper} from './fault-duplicate-wizard-stepper.service';
import {AttachmentApiService} from '@docma-mm-core/attachment/attachment-api.service';
import {AttachmentService} from '@docma-mm-core/attachment/attachment.service';
import {FaultService} from '@docma-mm-core/fault/fault.service';
import {MmFaultsSyncService} from '../../mm-faults-sync-service';
import {MmFaultsLoaderService} from '../../mm-faults-loader.service';
import {MmFilterService} from '../../../mm-filter/mm-filter.service';
import {Pool} from '../../../mm-common/pool/pool';
import {MmProjectPermissionsService} from '../../../mm-project/mm-project-permissions-service';
import {MydocmaPromptButtonTypeEnum, PromptDialogService} from '@mydocma/component-library/src/lib/prompt-dialog';
import {TranslateService} from '@ngx-translate/core';
import {PoolEditViewService} from '../../../mm-filter/pool/services/pool-edit-view.service';
import {PoolEditDataService} from '../../../mm-filter/pool/services/pool-edit-data.service';
import {MmViewService} from '../../../mm-view.service';
import {ProjectSettingEnum} from '@docma-mm-core/project/configuration/project-setting.enum';
import {LoadStatusEnum} from '@docma-mm-core/model/load-state/load-state.enum';
import {MmProjectLoaderService} from '../../../mm-project/mm-project-loader.service';
import {ConnectivityService} from '@mydocma/core';
import {PlatformModeStateService} from '../../../mm-offline/services/platform-mode-state.service';
import {AttachmentDbService} from '@docma-mm-core/attachment/attachment-db.service';
import {MmFaultPinService} from '../../mm-fault/services/mm-fault-pin.service';

export interface DuplicationPayload {
  originalFault: ViewFault;
  targetStructures: Structure[];
  selectedAttachments: Attachment[];
  duplicateCount?: number;
}

export interface DuplicationResult {
  success: boolean;
  createdFaults: FaultData[];
  createdFaultObjects: ViewFault[];
  errors: string[];
  faultsWithTrimmedAgNumber?: ViewFault[];
  faultsNotAddedToPools?: ViewFault[];
}

interface UniqueValueResult {
  formattedValue: string;
  trimmed: boolean;
}

@Injectable()
export class FaultDuplicateWizardDataService {

  private readonly AG_NUMBER_MAX_LENGTH = 64;
  private readonly DUPLICATE_UNIQUE_KEY_DEFAULT_LENGTH = 6;

  private addFaultToActivePools = false;
  private activePoolsForAddition: Pool[] = [];
  private shouldHideFaultsFromView = new Map<string, boolean>();
  private faultsToHideAfterSync: ViewFault[] = [];

  private uniqueCustomerNumber: boolean;

  constructor(private logger: Logger,
              private stepperService: FaultDuplicateWizardStepperService,
              private attachmentApiService: AttachmentApiService,
              private attachmentService: AttachmentService,
              private faultService: FaultService,
              private faultsLoaderService: MmFaultsLoaderService,
              private faultsSyncService: MmFaultsSyncService,
              private filterService: MmFilterService,
              private permissionService: MmProjectPermissionsService,
              private dialogService: PromptDialogService,
              private translateService: TranslateService,
              private poolEditViewService: PoolEditViewService,
              private poolEditDataService: PoolEditDataService,
              private mmViewService: MmViewService,
              private readonly projectLoaderService: MmProjectLoaderService,
              private readonly connectivityService: ConnectivityService,
              private readonly platformModeStateService: PlatformModeStateService,
              private readonly attachmentDbService: AttachmentDbService,
              private readonly faultPinService: MmFaultPinService) {

  }

  async duplicateFaultsFromCurrentState(): Promise<DuplicationResult> {
    const stepperData = this.stepperService.getCompleteData();
    const payload = this.createPayloadFromStepperData(stepperData);
    return this.duplicateFaults(payload);
  }

  private createPayloadFromStepperData(stepperData: IDuplicateWizardStepper): DuplicationPayload {
    return {
      originalFault: stepperData.originalFault,
      targetStructures: stepperData.locationStep.selectedStructures,
      selectedAttachments: stepperData.attachmentStep.selectedAttachments,
      duplicateCount: stepperData.optionsStep.duplicateType === 'single'
        ? stepperData.optionsStep.duplicatesToCreate
        : 1
    };
  }

  async duplicateFaults(payload: DuplicationPayload): Promise<DuplicationResult> {
    const projectParam = await firstValueFrom(this.projectLoaderService.projectSettings.pipe(filter(m => m.loadState.status === LoadStatusEnum.Loaded))
      .pipe(switchMap(projectSettings => this.projectLoaderService.projectUserPermissions.pipe(filter(m => m.loadState.status === LoadStatusEnum.Loaded))
        .pipe(map(projectUserPermissions => ({projectSettings, projectUserPermissions})
        ))
      )));
    this.uniqueCustomerNumber = JSON.parse(projectParam.projectSettings.data.find(x => x.Key === ProjectSettingEnum.UniqueCustomerNumber)?.Setting?.toString()?.toLowerCase() ?? 'false');

    this.shouldHideFaultsFromView.clear();
    this.faultsToHideAfterSync = [];

    const result: DuplicationResult = {
      success: false,
      createdFaults: [],
      createdFaultObjects: [],
      errors: [],
      faultsWithTrimmedAgNumber: [],
      faultsNotAddedToPools: []
    };

    try {
      const { payloads: faultsToCreate, trimmedIndices } = this.prepareFaultPayloads(payload);
      const stepperData = this.stepperService.getCompleteData();
      const projectUid = stepperData.project?.MyDocmaUid;
      const project = stepperData.project;

      const poolAdditionState = await this.checkAndHandlePoolAdditionForMultipleFaults(faultsToCreate.length);

      // separate faults with and without attachments for optimized processing
      const hasAttachments = payload.selectedAttachments?.length > 0;

      if (hasAttachments) {
        await this.processFaultsWithAttachments(faultsToCreate, trimmedIndices, projectUid, project, poolAdditionState, payload.selectedAttachments, result);
      } else {
        await this.processFaultsWithoutAttachments(faultsToCreate, trimmedIndices, projectUid, project, poolAdditionState, result);
      }

      result.success = result.createdFaultObjects.length > 0;

    } catch (error) {
      const errorMessage = `Failed to prepare fault duplication: ${error?.message || error}`;
      this.logger.error(errorMessage, '', 'FaultDuplicateWizardDataService');
      result.errors.push(errorMessage);
      throw error;
    }

    return result;
  }

  private async processFaultsWithAttachments(
    faultsToCreate: any[],
    trimmedIndices: number[],
    projectUid: string,
    project: any,
    poolAdditionState: {shouldAdd: boolean, pools: Pool[]},
    selectedAttachments: Attachment[],
    result: any
  ): Promise<void> {
    const FAULT_CREATION_DELAY_MS = 100;

    const readyFaults = new Map<string, ViewFault>();
    const readyFiles = new Map<string, File>();
    const pendingDistributions = new Set<string>();

    const targetFaults: ViewFault[] = [];

    const pendingAttachmentPromises: Promise<void>[] = [];

    const tryDistributeAttachments = () => {
      if (readyFaults.size === 0 || readyFiles.size === 0) {
        return;
      }

      readyFiles.forEach((file, attachmentUid) => {
        const sourceAtt = selectedAttachments.find(a => a.uid === attachmentUid);
        if (!sourceAtt) { return; }

        readyFaults.forEach((viewFault, faultUid) => {
          const distributionKey = `${faultUid}:${attachmentUid}`;
          if (pendingDistributions.has(distributionKey)) {
            return;
          }
          pendingDistributions.add(distributionKey);

          const attachmentPromise = (async () => {
            try {
              const sourceData: any = {};
              const attData = (sourceAtt as any)?.data || {};
              const allowedFields = ['Description', 'Type', 'VisibleFor', 'EditableFor'];
              allowedFields.forEach(key => {
                if (attData[key] !== undefined) {
                  sourceData[key] = attData[key];
                }
              });

              const attachmentData = new AttachmentData(sourceData);
              const newAttachment = await this.attachmentService.createNewAttachment(attachmentData, file);

              viewFault.addAttachment(newAttachment);
              this.faultService.faultChangedSubject.next(viewFault);
            } catch (e) {
              this.logger.error(e, '', 'FaultDuplicateWizardDataService');
              pendingDistributions.delete(distributionKey);
            }
          })();

          pendingAttachmentPromises.push(attachmentPromise);
        });
      });
    };

    const waitForAllAttachments = async () => {
      await Promise.all(pendingAttachmentPromises);
    };

// TODO: optmize by prefetching the attachments if they are available from somewhere else.
// seems the duplication wizard attachmets are missing the file - investigate
    const downloadPromise = (async () => {
      const cachedFiles = await this.preDownloadAttachmentsWithCallback(selectedAttachments, projectUid, (uid, file) => {
        readyFiles.set(uid, file);
        tryDistributeAttachments();
      });
      return cachedFiles;
    })();

    // create faults while downloads happen
    for (let i = 0; i < faultsToCreate.length; i++) {
      const faultPayload = faultsToCreate[i];

      const faultResult = await this.createAndProcessFault(
        faultPayload,
        i,
        trimmedIndices,
        projectUid,
        project,
        poolAdditionState,
        null,
        result,
        true
      );

      if (faultResult.success && faultResult.viewFault && selectedAttachments?.length) {
        let targetFault: ViewFault = faultResult.viewFault;

        try {
          const uiFault = this.faultsLoaderService.getFault(faultResult.viewFault.uid);
          if (uiFault) {
            targetFault = uiFault;
          }
        } catch (fetchErr) {
          console.warn('[Duplicate Wizard] Could not fetch UI fault:', fetchErr);
        }

        readyFaults.set(targetFault.uid, targetFault);
        targetFaults.push(targetFault);

        tryDistributeAttachments();
      }

      if (i < faultsToCreate.length - 1) {
        await new Promise(resolve => setTimeout(resolve, FAULT_CREATION_DELAY_MS));
      }
    }


    await downloadPromise;
    tryDistributeAttachments();
    await waitForAllAttachments();

    const SYNC_TIMEOUT_MS = 20000;
    const syncPromises = targetFaults.map(fault => {
      return Promise.race<ViewFault | null>([
        firstValueFrom(merge(
          this.faultsSyncService.onNewFaultSynced,
          this.faultsSyncService.onFaultSynced
        ).pipe(
          filter(f => !!f && f.uid === fault.uid)
        )),
        new Promise<null>(resolve => setTimeout(() => resolve(null), SYNC_TIMEOUT_MS))
      ]);
    });

    for (const fault of targetFaults) {
      try {
        await this.faultService.saveFaultChanges(projectUid, fault);
        await this.setPinTextValue(fault);
      } catch (e) {
        console.warn('[Duplicate Wizard] failed to trigger save for fault:', fault.uid, e);
      }
    }

    const syncResults = await Promise.all(syncPromises);
    const syncedFaults: ViewFault[] = [];

    for (let i = 0; i < syncResults.length; i++) {
      const syncedFault = syncResults[i];
      if (syncedFault && syncedFault.id) {
        syncedFaults.push(syncedFault);
        console.log(`[Duplicate Wizard] Fault synced with ID ${syncedFault.id}, now syncing ${syncedFault.attachments.length} attachments with throttling...`);

        try {
          await this.syncAttachmentsWithThrottling(projectUid, syncedFault);
        } catch (e) {
          console.warn('[Duplicate Wizard] failed to sync attachments for fault:', syncedFault.uid, e);
        }
      } else {
        console.warn('[Duplicate Wizard] Fault did not sync in time:', targetFaults[i]?.uid);
      }
    }

    if (poolAdditionState.shouldAdd && poolAdditionState.pools.length > 0 && syncedFaults.length > 0) {
      try {
        const poolIds = poolAdditionState.pools.map(p => p.Id);
        const { successfulPoolIds } = await this.poolEditViewService.poolFaultToggle(syncedFaults, poolIds, true);

        if (successfulPoolIds && successfulPoolIds.length > 0) {
          try {
            const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
            const updatedPools = await this.poolEditDataService.getPoolsWithUpdatedCounts(
              currentProject.Id,
              successfulPoolIds
            );
            const poolCountChanges = updatedPools.map(pool => ({
              poolId: pool.Id,
              newCount: pool.Count
            }));
            this.faultsLoaderService.emitPoolCountUpdated(poolCountChanges);
          } catch (error) {
            this.logger.error(error, '', 'FaultDuplicateWizardDataService');
          }
        }
      } catch (error) {
        console.warn('[Duplicate Wizard] Failed to add faults to pools:', error);
      }
    }

    for (const fault of targetFaults) {
      if (this.shouldHideFaultsFromView.get(fault.uid)) {
        const faultToHide = syncedFaults.find(f => f.uid === fault.uid) || this.faultsLoaderService.getFault(fault.uid) || fault;
        this.triggerFaultRemovalFromView(faultToHide);
        this.shouldHideFaultsFromView.delete(fault.uid);
      }
    }

    // refresh layout
    try {
      const layoutObs = await this.faultsLoaderService.loadLayoutFaults(targetFaults, true);
      await layoutObs.toPromise();
    } catch (layoutErr) {
      console.warn('[Duplicate Wizard] Failed to refresh layout:', layoutErr);
    }
  }

  private async processFaultsWithoutAttachments(
    faultsToCreate: any[],
    trimmedIndices: number[],
    projectUid: string,
    project: any,
    poolAdditionState: {shouldAdd: boolean, pools: Pool[]},
    result: any
  ): Promise<void> {
    const FAULT_CREATION_DELAY_MS = 100;

    for (let i = 0; i < faultsToCreate.length; i++) {
      const faultPayload = faultsToCreate[i];

      await this.createAndProcessFault(
        faultPayload,
        i,
        trimmedIndices,
        projectUid,
        project,
        poolAdditionState,
        null,
        result
      );

      if (i < faultsToCreate.length - 1) {
        await new Promise(resolve => setTimeout(resolve, FAULT_CREATION_DELAY_MS));
      }

    }
  }

  private async preDownloadAttachmentsWithCallback(
    sourceAttachments: Attachment[],
    projectUid: string,
    onFileReady: (uid: string, file: File) => void
  ): Promise<Map<string, File>> {
    const cachedFiles = new Map<string, File>();
    const CONCURRENT_DOWNLOADS = 10;
    const downloadAttachment = async (sourceAtt: Attachment): Promise<{ uid: string, file: File | null }> => {
      try {
        let file: File = null;

        if (sourceAtt.source?.loadState?.data && sourceAtt.source.loadState.data instanceof File) {
          file = sourceAtt.source.loadState.data;
        } else {
          // if file not available, download it
          // todo optimize
          const sourceId = (sourceAtt as any)?.data?.Id ?? (sourceAtt as any)?.id;
          if (this.platformModeStateService.isLocalMode && this.connectivityService.isOfflineVal){
            file = await this.attachmentDbService.getFile(projectUid, sourceAtt.uid);
          } else{
            file = await this.attachmentApiService.getAttachment(sourceId, true);
          }

        }
        return { uid: sourceAtt.uid, file };
      } catch (e) {
        this.logger.error(e, '', 'FaultDuplicateWizardDataService');
        return { uid: sourceAtt.uid, file: null };
      }
    };

    for (let i = 0; i < sourceAttachments.length; i += CONCURRENT_DOWNLOADS) {
      const chunk = sourceAttachments.slice(i, i + CONCURRENT_DOWNLOADS);
      const chunkResults = await Promise.all(chunk.map(att => downloadAttachment(att)));

      chunkResults.forEach(result => {
        if (result.file) {
          cachedFiles.set(result.uid, result.file);
          onFileReady(result.uid, result.file);
        } else {
          console.warn('[Duplicate Wizard] Could not get file for attachment:', result.uid);
        }
      });

    }

    return cachedFiles;
  }


  private async createAndProcessFault(
    faultPayload: any,
    globalIdx: number,
    trimmedIndices: number[],
    projectUid: string,
    project: any,
    poolAdditionState: {shouldAdd: boolean, pools: Pool[]},
    selectedAttachments: Attachment[] | null,
    result: any,
    skipPostSync = false
  ): Promise<{ success: boolean; viewFault?: ViewFault; error?: string }> {
    try {
      const localFault = await this.faultService.createNewFault(projectUid, faultPayload);
      const viewFault = new ViewFault(localFault);
      viewFault.project = project;
      viewFault.unsavedNew = true;

      result.createdFaultObjects.push(viewFault);
      result.createdFaults.push(viewFault.data);

      if (trimmedIndices.includes(globalIdx)) {
        result.faultsWithTrimmedAgNumber.push(viewFault);
      }

      try {
        await this.faultsLoaderService.addFault(viewFault);
      } catch (e) {
        console.warn('[Duplicate Wizard] Failed to add new fault to loader', e);
      }

      if (!poolAdditionState.shouldAdd && poolAdditionState.pools.length > 0) {
        this.shouldHideFaultsFromView.set(viewFault.uid, true);
        result.faultsNotAddedToPools.push(viewFault);
      }

      if (!skipPostSync) {
        try {
          await this.faultService.saveFaultChanges(projectUid, viewFault);
          await this.setPinTextValue(viewFault);
        } catch (syncError) {
          console.warn('[Duplicate Faults] Failed to trigger initial sync:', syncError);
        }

        if (poolAdditionState.shouldAdd || this.shouldHideFaultsFromView.has(viewFault.uid)) {
          try {
            await this.handlePostSyncOperations(viewFault.uid, selectedAttachments, poolAdditionState);
          } catch (e) {
            console.warn('[Duplicate Faults] Background operations failed:', e);
          }
        }
      }
      return { success: true, viewFault };
    } catch (error) {
      const errorMessage = `Failed to create fault for structure ${faultPayload.Structure.Id}: ${error?.message || error}`;
      this.logger.error(error, '', 'FaultDuplicateWizardDataService');
      result.errors.push(errorMessage);
      return { success: false, error: errorMessage };
    }
  }

  private async handlePostSyncOperations(
    newFaultUid: string,
    selectedAttachments: Attachment[],
    poolAdditionState: {shouldAdd: boolean, pools: Pool[]}
  ): Promise<void> {
    try {
      const syncedFault = await Promise.race<Promise<ViewFault | null>>([
        firstValueFrom(this.faultsSyncService.onNewFaultSynced.pipe(
          map(f => f),
          filter(f => !!f && f.uid === newFaultUid)
        )),
        new Promise<ViewFault | null>(resolve => setTimeout(() => resolve(null), 20000))
      ] as any);

      if (!syncedFault) {
        console.warn('[fault-duplicate-wizard.data.service] Fault sync timeout, cannot proceed with post-sync operations');
        try {
          if (!poolAdditionState.shouldAdd && this.shouldHideFaultsFromView.get(newFaultUid)) {
            const fallbackFault = this.faultsLoaderService.getFault(newFaultUid);
            if (fallbackFault) {
              this.triggerFaultRemovalFromView(fallbackFault);
            }
          }
        } catch (fallbackError) {
          console.warn('[fault-duplicate-wizard.data.service] Fallback hide after sync timeout failed:', fallbackError);
        }
        return;
      }

      if (!syncedFault?.id) {
        console.warn('[fault-duplicate-wizard.data.service] Synced fault has no ID, cannot proceed with post-sync operations');
        return;
      }

      console.log('[Duplicate Wizard] Synced fault received:', syncedFault.uid, syncedFault.id);

      if (poolAdditionState.shouldAdd && poolAdditionState.pools.length > 0) {
        try {
          const poolIds = poolAdditionState.pools.map(p => p.Id);
          const { successfulPoolIds } = await this.poolEditViewService.poolFaultToggle(
            [syncedFault],
            poolIds,
            true
          );

          if (successfulPoolIds && successfulPoolIds.length > 0) {
            try {
              const currentProject = await firstValueFrom(this.mmViewService.currentProject$);
              const updatedPools = await this.poolEditDataService.getPoolsWithUpdatedCounts(
                currentProject.Id,
                successfulPoolIds
              );
              const poolCountChanges = updatedPools.map(pool => ({
                poolId: pool.Id,
                newCount: pool.Count
              }));
              this.faultsLoaderService.emitPoolCountUpdated(poolCountChanges);
            } catch (error) {
              this.logger.error(error, '', 'FaultDuplicateWizardDataService');
            }
          }

          this.shouldHideFaultsFromView.delete(syncedFault.uid);
        } catch (error) {
          this.logger.error(error, '', 'FaultDuplicateWizardDataService');
        }
      } else if (this.shouldHideFaultsFromView.get(newFaultUid)) {
        this.triggerFaultRemovalFromView(syncedFault);
        setTimeout(() => {
          try {
            const maybeStillVisible = this.faultsLoaderService.getFault(newFaultUid);
            if (maybeStillVisible) {
              this.triggerFaultRemovalFromView(maybeStillVisible);
            }
          } catch (e) {
            console.warn('[fault-duplicate-wizard.data.service] Post-removal re-check failed:', e);
          }
        }, 0);
      }

      if (selectedAttachments?.length) {
        let targetFault: ViewFault = syncedFault;
        try {
          const uiFault = this.faultsLoaderService.getFault(syncedFault.uid);
          if (uiFault) {
            targetFault = uiFault;
          }
        } catch (fetchErr) {
          console.warn('[Duplicate Wizard] Could not fetch UI fault, using synced fault:', fetchErr);
        }

        await this.duplicateAttachmentsForFault(targetFault, selectedAttachments);

        const projectUid = targetFault.project?.MyDocmaUid;
        if (projectUid) {
          await this.faultService.saveFaultChanges(projectUid, targetFault);
          await this.setPinTextValue(targetFault);
        }

        try {
          const layoutObs = await this.faultsLoaderService.loadLayoutFaults([targetFault], true);
          await layoutObs.toPromise();
        } catch (layoutErr) {
          console.warn('[Duplicate Wizard] Failed to refresh layout data:', layoutErr);
        }
      }
    } catch (e) {
      console.warn('[fault-duplicate-wizard.data.service] handlePostSyncOperations error:', e);
    }
  }

  private prepareFaultPayloads(payload: DuplicationPayload): { payloads: FaultData[], trimmedIndices: number[] } {
    const {originalFault, targetStructures, selectedAttachments, duplicateCount = 1} = payload;
    const baseFaultData = JSON.parse(JSON.stringify(originalFault.data));
    delete baseFaultData.ModifiedOn;
    delete baseFaultData.CreatedOn;
    delete baseFaultData.ModifiedOnMobile;
    delete baseFaultData.CreatedOnMobile;
    delete baseFaultData.Structure;
    if (baseFaultData.Parent) {
      delete baseFaultData.Parent;
    }

    const payloads: FaultData[] = [];
    const trimmedIndices: number[] = [];

    if (duplicateCount > 1 && targetStructures.length === 1) {
      const targetStructure = targetStructures[0];
      for (let i = 0; i < duplicateCount; i++) {
        const trimmedInfo = { wasTrimmed: false };
        const faultPayload = this.createFaultPayload(baseFaultData, targetStructure, selectedAttachments, i + 1, trimmedInfo);
        payloads.push(faultPayload);

        if (trimmedInfo.wasTrimmed) {
          trimmedIndices.push(i);
        }
      }
    } else {
      targetStructures.forEach((structure, index) => {
        const trimmedInfo = { wasTrimmed: false };
        const faultPayload = this.createFaultPayload(baseFaultData, structure, selectedAttachments, index + 1, trimmedInfo);
        payloads.push(faultPayload);

        if (trimmedInfo.wasTrimmed) {
          trimmedIndices.push(index);
        }
      });
    }

    return { payloads, trimmedIndices };
  }

  private createBaseFaultData(originalFault: ViewFault): FaultData {
    const baseFaultData = JSON.parse(JSON.stringify(originalFault.data));
    delete baseFaultData.Creator;
    delete baseFaultData.Modifier;
    delete baseFaultData.ModifiedOn;
    delete baseFaultData.CreatedOn;
    delete baseFaultData.ModifiedOnMobile;
    delete baseFaultData.CreatedOnMobile;
    delete baseFaultData.Structure;
    delete baseFaultData.UniqueId;
    delete baseFaultData.SUid;
    return baseFaultData;
  }

  private createFaultPayload(baseFaultData: FaultData,
                             targetStructure: Structure,
                             _selectedAttachments?: Attachment[],
                             _copyNumber?: number,
                             _trimmedInfo?: { wasTrimmed: boolean }): FaultData {

    const faultPayload = new FaultData();

    Object.assign(faultPayload, baseFaultData);
    // Number2 is the AG number
    // seems has to be unique for each fault
    if (faultPayload.Number2) {


      if (faultPayload.Number2 != faultPayload.Number2Prefix && (!!faultPayload.Number2Prefix || !!faultPayload.Number2Suffix)) {
        delete faultPayload.Number2;
      }
      else if (!!faultPayload.Number2 && !isNaN(+faultPayload.Number2)) {
        delete faultPayload.Number2Prefix;
        delete faultPayload.Number2Suffix;
        faultPayload.Number2 = '#';
      }
      else if (!!faultPayload.Number2 && !!isNaN(+faultPayload.Number2)) {
        if (!!this.uniqueCustomerNumber) {
          faultPayload.Number2Prefix = faultPayload.Number2;
          delete faultPayload.Number2;
        }

        // const result = this.getUniqueValueForDuplicate(
        //   faultPayload.Number2,
        //   this.AG_NUMBER_MAX_LENGTH,
        //   this.DUPLICATE_UNIQUE_KEY_DEFAULT_LENGTH
        // );
        // faultPayload.Number2 = result.formattedValue;
        //
        // if (trimmedInfo) {
        //   trimmedInfo.wasTrimmed = result.trimmed;
        // }
      }
      else {
        console.warn('duplicate defect: number2 not changed!');
      }


    }

    const minimalStructure: any = {
      Id: targetStructure?.Id
    };
    faultPayload.Structure = minimalStructure as any;


    delete (faultPayload as any).Id;
    delete (faultPayload as any).UniqueId;
    delete (faultPayload as any).SUid;
    delete (faultPayload as any).Number1;
    delete (faultPayload as any).Creator;
    delete (faultPayload as any).Modifier;
    delete (faultPayload as any).CreatedOn;
    delete (faultPayload as any).ModifiedOn;
    delete (faultPayload as any).Activities;
    delete (faultPayload as any).Files;
    delete (faultPayload as any).Attachments;

    return faultPayload;
  }
// TODO legacy code, cleanup
  private async duplicateAttachmentsForFault(newFault: ViewFault, sourceAttachments: Attachment[]): Promise<Attachment[]> {
    const created: Attachment[] = [];

    // process attachments sequentially to avoid overwhelming the server
    for (const sourceAtt of sourceAttachments) {
      try {
        const sourceId = (sourceAtt as any)?.data?.Id ?? (sourceAtt as any)?.id;
        const file: File = await this.attachmentApiService.getAttachment(sourceId, true);

        if (!file) {
          console.warn('[Duplicate Wizard] Could not download source attachment:', sourceAtt.uid);
          continue;
        }

        const sourceData: any = {};
        const attData = (sourceAtt as any)?.data || {};
        Object.keys(attData).forEach(key => {
          if (typeof attData[key] !== 'function' &&
              key !== 'Id' && key !== 'UniqueId' && key !== 'SUid' &&
              key !== 'Fault' && key !== 'FaultId' &&
              key !== 'ActivityId' && key !== 'Files') {
            try {
              sourceData[key] = attData[key];
            } catch (e) {
              console.warn('[Duplicate Wizard] Error cloning attachment property:', key, e);
            }
          }
        });

        if (!sourceData.Description) {
          sourceData.Description = file.name;
        }

        const attachmentData = new AttachmentData(sourceData);

        const newAttachment = await this.attachmentService.createNewAttachment(attachmentData, file);

        newFault.addAttachment(newAttachment);
        created.push(newAttachment);

        this.faultService.faultChangedSubject.next(newFault);

      } catch (e) {
        this.logger.error(e, '', 'FaultDuplicateWizardDataService');
      }
    }

    return created;
  }

  canDuplicatePlanSections(originalStructure: Structure, targetStructures: Structure[]): boolean {
    return targetStructures.every(target => target.Id === originalStructure?.Id);
  }

  getValidAttachmentsForDuplication(
    allAttachments: Attachment[],
  ): Attachment[] {
    if (!allAttachments || allAttachments.length === 0) {
      return [];
    }

    const stepperData = this.stepperService.getCompleteData();
    const originalStructure = stepperData.originalFault?.data?.Structure;
    const targetStructures = stepperData.locationStep?.selectedStructures || [];

    const canDuplicatePlanSections = this.canDuplicatePlanSections(originalStructure, targetStructures);

    const filteredAttachments = allAttachments.filter(attachment => {
      const attachmentType = attachment?.data?.Type;

      if (attachmentType === AttachmentType.PlanSection && !canDuplicatePlanSections) {
        return false;
      }

      return true;
    });

    return filteredAttachments;
  }

  private async checkAndHandlePoolAdditionForMultipleFaults(faultCount: number): Promise<{shouldAdd: boolean, pools: Pool[]}> {
    try {
      const filterState = await firstValueFrom(this.filterService.state);
      const activeInclusionPools = filterState?.poolInclusionFilters;

      if (!activeInclusionPools || activeInclusionPools.length === 0) {
        return { shouldAdd: false, pools: [] };
      }

      const hasPoolPermissions = await firstValueFrom(this.permissionService.permissionDefectPoolsAddDefects$);
      if (!hasPoolPermissions) {
        console.warn('checkAndHandlePoolAdditionForMultipleFaults: No pool permissions for duplicated faults');
        return { shouldAdd: false, pools: activeInclusionPools };
      }

      const isMultiplePools = activeInclusionPools.length > 1;
      const isMultipleFaults = faultCount > 1;

      let title: string;
      let content: string;

      if (isMultiplePools) {
        title = isMultipleFaults
          ? this.translateService.instant('MM.pool.listToggle.addToPoolsDialog.titlePlural', { count: faultCount })
          : this.translateService.instant('MM.pool.listToggle.addToPoolsDialog.title');
        content = isMultipleFaults
          ? this.translateService.instant('MM.pool.listToggle.addToPoolsDialog.contentPlural', { count: faultCount })
          : this.translateService.instant('MM.pool.listToggle.addToPoolsDialog.content');
      } else {
        title = isMultipleFaults
          ? this.translateService.instant('MM.pool.listToggle.addToPoolDialog.titlePlural', { count: faultCount })
          : this.translateService.instant('MM.pool.listToggle.addToPoolDialog.title');
        content = isMultipleFaults
          ? this.translateService.instant('MM.pool.listToggle.addToPoolDialog.contentPlural', { count: faultCount })
          : this.translateService.instant('MM.pool.listToggle.addToPoolDialog.content');
      }

      const result = await this.dialogService.showPromptDialog({
        title,
        content,
        buttons: [
          {
            text: this.translateService.instant('MM.general.button.no'),
            type: MydocmaPromptButtonTypeEnum.default,
            value: false
          },
          {
            text: this.translateService.instant('MM.general.button.yes'),
            type: MydocmaPromptButtonTypeEnum.primary,
            value: true
          }
        ]
      });

      if (result?.value) {
        this.addFaultToActivePools = true;
        this.activePoolsForAddition = activeInclusionPools;
        return { shouldAdd: true, pools: activeInclusionPools };
      } else {
        return { shouldAdd: false, pools: activeInclusionPools };
      }
    } catch (error) {
      this.logger.error(error, '', 'FaultDuplicateWizardDataService');
      return { shouldAdd: false, pools: [] };
    }
  }

  // private async checkAndHandlePoolAddition(fault: ViewFault): Promise<void> {
  //   try {
  //     this.addFaultToActivePools = false;
  //     this.activePoolsForAddition = [];

  //     const filterState = await firstValueFrom(this.filterService.state);
  //     const activeInclusionPools = filterState?.poolInclusionFilters;

  //     if (!activeInclusionPools || activeInclusionPools.length === 0) {
  //       return;
  //     }

  //     const hasPoolPermissions = await firstValueFrom(this.permissionService.permissionDefectPoolsAddDefects$);
  //     if (!hasPoolPermissions) {
  //       console.warn('checkAndHandlePoolAddition: No pool permissions for duplicated fault');
  //       return;
  //     }

  //     const isMultiplePools = activeInclusionPools.length > 1;
  //     const title = isMultiplePools
  //       ? this.translateService.instant('MM.pool.listToggle.addToPoolsDialog.title')
  //       : this.translateService.instant('MM.pool.listToggle.addToPoolDialog.title');

  //     const content = isMultiplePools
  //       ? this.translateService.instant('MM.pool.listToggle.addToPoolsDialog.content')
  //       : this.translateService.instant('MM.pool.listToggle.addToPoolDialog.content');

  //     const result = await this.dialogService.showPromptDialog({
  //       title,
  //       content,
  //       buttons: [
  //         {
  //           text: this.translateService.instant('MM.general.button.no'),
  //           type: MydocmaPromptButtonTypeEnum.default,
  //           value: false
  //         },
  //         {
  //           text: this.translateService.instant('MM.general.button.yes'),
  //           type: MydocmaPromptButtonTypeEnum.primary,
  //           value: true
  //         }
  //       ]
  //     });

  //     if (result?.value) {
  //       this.addFaultToActivePools = true;
  //       this.activePoolsForAddition = activeInclusionPools;
  //       this.shouldHideFaultsFromView.delete(fault.uid);
  //     } else {
  //       this.shouldHideFaultsFromView.set(fault.uid, true);
  //       this.faultsToHideAfterSync.push(fault);
  //     }
  //   } catch (error) {
  //     console.error('Error checking pool addition dialog for duplicated fault:', error);
  //   }
  // }

  private triggerFaultRemovalFromView(faultToHide: ViewFault): void {
    if (faultToHide) {
      this.faultsLoaderService.mmFaultsCountService.decreaseVisibleNewFaults(1);
      this.faultsLoaderService.removeFaultFromView(faultToHide);
    }
  }


  // sync with throttling to prevent server overload - multiple small syncs instead one big with all
  private async syncAttachmentsWithThrottling(projectUid: string, fault: ViewFault): Promise<void> {
    const ATTACHMENTS_PER_BATCH = 3;
    const DELAY_BETWEEN_BATCHES_MS = 300;

    const totalAttachments = fault.attachments.length;
    if (totalAttachments === 0) {
      return;
    }

    for (let i = 0; i < totalAttachments; i += ATTACHMENTS_PER_BATCH) {
      const batchNumber = Math.floor(i / ATTACHMENTS_PER_BATCH) + 1;
      const totalBatches = Math.ceil(totalAttachments / ATTACHMENTS_PER_BATCH);

      try {
        await this.faultService.saveFaultChanges(projectUid, fault);
        await this.setPinTextValue(fault);

        if (i + ATTACHMENTS_PER_BATCH < totalAttachments) {
          await new Promise(resolve => setTimeout(resolve, DELAY_BETWEEN_BATCHES_MS));
        }
      } catch (e) {
        console.warn(`[Duplicate Wizard] Failed to sync batch ${batchNumber}/${totalBatches}:`, e);
      }
    }
  }

  private async setPinTextValue(viewFault: ViewFault) {
    const pinTextValue = await this.faultPinService.getPinTextValue(viewFault.data);
    if (pinTextValue) {
      viewFault.pinText = (Object.values(pinTextValue)[0]?.length > 25 ? Object.values(pinTextValue)[0].slice(0, 25) + '...' : Object.values(pinTextValue)[0]);
    }
  }

}
